/**
 * PolicyComplianceTab — acknowledgment compliance reporting with drill-down.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, CheckCircle2, Clock3, AlertTriangle, Percent, RefreshCw, FileDown } from 'lucide-react';
import ComplianceReportDialog from './ComplianceReportDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const fmt = (d) => d ? formatDateOnly(d) : '—';

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <Card><CardContent className="p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-900">{value}</p>
    </div>
  </CardContent></Card>
);

const STATUS_BADGE = {
  acknowledged: 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  pending: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100',
  overdue: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-100',
};

export default function PolicyComplianceTab() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ policy_id: 'all', role: 'all', team_id: 'all', status: 'all', date_from: '', date_to: '', q: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.policy_id !== 'all') params.policy_id = filters.policy_id;
      if (filters.role !== 'all') params.role = filters.role;
      if (filters.team_id !== 'all') params.team_id = filters.team_id;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.q) params.q = filters.q;  // POLICY-COMPLIANCE-REPORT-001 — server-side, same rule as the export
      const [sRes, dRes] = await Promise.all([
        axios.get(`${API}/policies/compliance/summary`),
        axios.get(`${API}/policies/compliance/detail`, { params }),
      ]);
      setSummary(sRes.data);
      setRows(dRes.data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, oRes] = await Promise.all([
          axios.get(`${API}/policies`),
          axios.get(`${API}/policies/meta/options`),
        ]);
        setPolicies((pRes.data.policies || []).filter((p) => p.ack_required));
        setOptions(oRes.data);
      } catch { /* summary fetch will surface errors */ }
    })();
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const visible = rows;  // search now applied server-side (build_compliance_rows q) — screen and export share one rule
  const { hasPermission } = usePermissions();
  const canExport = hasPermission('policy_compliance_report', 'export');
  const [reportOpen, setReportOpen] = useState(false);
  const reportParams = Object.fromEntries(Object.entries({
    policy_id: filters.policy_id !== 'all' ? filters.policy_id : '', role: filters.role !== 'all' ? filters.role : '',
    team_id: filters.team_id !== 'all' ? filters.team_id : '', status: filters.status !== 'all' ? filters.status : '',
    date_from: filters.date_from, date_to: filters.date_to, q: filters.q,
  }).filter(([, v]) => v));

  return (
    <div className="space-y-5" data-testid="policy-compliance-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Total Policies" value={summary?.total_policies ?? '—'} tone="bg-slate-100 text-slate-600" />
        <StatCard icon={CheckCircle2} label="Acknowledged" value={summary?.acknowledged ?? '—'} tone="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Clock3} label="Pending" value={summary?.pending ?? '—'} tone="bg-amber-100 text-amber-600" />
        <StatCard icon={AlertTriangle} label="Overdue" value={summary?.overdue ?? '—'} tone="bg-red-100 text-red-600" />
        <StatCard icon={Percent} label="Acknowledgment %" value={summary ? `${summary.ack_percentage}%` : '—'} tone="bg-blue-100 text-blue-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <Select value={filters.policy_id} onValueChange={(v) => setFilters((f) => ({ ...f, policy_id: v }))}>
          <SelectTrigger className="w-52 h-9" data-testid="compliance-policy-filter"><SelectValue placeholder="Policy" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All policies</SelectItem>
            {policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.team_id} onValueChange={(v) => setFilters((f) => ({ ...f, team_id: v }))}>
          <SelectTrigger className="w-40 h-9" data-testid="compliance-team-filter"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            {(options?.teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.role} onValueChange={(v) => setFilters((f) => ({ ...f, role: v }))}>
          <SelectTrigger className="w-44 h-9" data-testid="compliance-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {(options?.roles || []).map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="w-40 h-9" data-testid="compliance-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <label className="text-[10px] text-slate-400 block">Assigned from</label>
          <Input type="date" className="w-36 h-9" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="compliance-date-from" />
        </div>
        <div>
          <label className="text-[10px] text-slate-400 block">to</label>
          <Input type="date" className="w-36 h-9" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="compliance-date-to" />
        </div>
        <Input placeholder="Search employee…" className="w-44 h-9" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="compliance-search-input" />
        <Button variant="outline" size="sm" className="h-9" onClick={fetchData} disabled={loading} data-testid="compliance-refresh-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
        {canExport && (
          <Button size="sm" className="h-9 ml-auto bg-blue-600 hover:bg-blue-700" onClick={() => setReportOpen(true)} data-testid="compliance-export-report-btn">
            <FileDown className="h-4 w-4 mr-1.5" /> Export Report
          </Button>
        )}
      </div>
      <ComplianceReportDialog open={reportOpen} onOpenChange={setReportOpen} params={reportParams} />

      {/* Drill-down table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <Table data-testid="compliance-table" className="tf-blue-head">
              <TableHeader>
                <TableRow className="bg-blue-600 hover:bg-blue-600 border-blue-600">
                  {['Employee', 'Team', 'Role', 'Policy', 'Version', 'Status', 'Assigned', 'Due', 'Acknowledged'].map((h) => (
                    <TableHead key={h} className="!text-white text-xs font-semibold uppercase tracking-wider">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">No records match the current filters.</TableCell></TableRow>
                )}
                {visible.map((r, i) => (
                  <TableRow key={`${r.user_id}-${r.policy_id}-${i}`} data-testid={`compliance-row-${r.user_id}-${r.policy_id}`}>
                    <TableCell>
                      <p className="text-sm font-semibold text-slate-900">{r.user_name}</p>
                      <p className="text-xs text-slate-400">{r.user_email}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{r.team_name || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-600 capitalize">{(r.role || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-sm text-slate-700">{r.policy_title}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.version_label ? `v${r.version_label}` : '—'}</TableCell>
                    <TableCell><Badge className={`text-xs capitalize ${STATUS_BADGE[r.status] || ''}`}>{r.status}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">{fmt(r.assigned_at)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmt(r.due_at)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.acknowledged_at ? formatDateTimeLocal(r.acknowledged_at) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
